<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Activity, AlertTriangle, Bot, Check, Layers3, ListTree, Sparkles, UsersRound } from 'lucide-vue-next'

import AlphaOverviewPanel from '@/components/AlphaOverviewPanel.vue'
import IntelligenceGraphPanel from '@/components/IntelligenceGraphPanel.vue'
import type {
  AlphaOverview,
  AnalysisResult,
  IntelligenceGraph,
  MonitorStatus,
} from '@/types/monitor'
import { formatLocalDateTime } from '@/utils/format'

const props = defineProps<{
  status: MonitorStatus
  analyses: AnalysisResult[]
  alphaOverview: AlphaOverview | null
  alphaLoading: boolean
  intelligenceGraph: IntelligenceGraph | null
  intelligenceLoading: boolean
  canWrite: boolean
}>()

const emit = defineEmits<{
  trigger: []
}>()

const { t } = useI18n()
const selectedId = ref<string | null>(null)

watch(
  () => props.analyses,
  (items) => {
    if (!selectedId.value && items[0]) selectedId.value = items[0].id
  },
  { immediate: true },
)

const selected = computed(() =>
  props.analyses.find((analysis) => analysis.id === selectedId.value) ?? props.analyses[0] ?? null,
)
const selectedTopics = computed(() => Object.entries(selected.value?.topic_summaries ?? {}).slice(0, 6))
const selectedSources = computed(() => Object.entries(selected.value?.source_breakdown ?? {}).slice(0, 8))
const selectedActors = computed(() => (selected.value?.key_actors ?? []).slice(0, 6))
const selectedTimeline = computed(() => (selected.value?.timeline ?? []).slice(0, 8))
const selectedContradictions = computed(() => (selected.value?.contradictions ?? []).slice(0, 5))

</script>

<template>
  <section class="tm-view tm-analysis-view">
    <header class="tm-page-header">
      <div>
        <h1>{{ t('analysisSettings.title') }}</h1>
        <p>{{ t('analysisSettings.description') }}</p>
      </div>
      <button v-if="canWrite" class="tm-button tm-button--primary" :disabled="status.analysis_running" @click="emit('trigger')">
        <Sparkles :size="17" />
        {{ status.analysis_running ? t('workspace.runningAnalysis') : t('analysisSettings.runNow') }}
      </button>
    </header>

    <div class="tm-analysis-status-strip">
      <div><span>{{ t('analysisSettings.autoStatus') }}</span><strong>{{ t('analysisSettings.resultsOnly') }}</strong></div>
      <div><span>{{ t('analysisSettings.pendingShort') }}</span><strong>{{ status.analysis_pending_messages.toLocaleString() }}</strong></div>
      <div><span>{{ t('workspace.nextAnalysis') }}</span><strong class="tm-analysis-status-time" :title="status.analysis_next_run_at ? formatLocalDateTime(status.analysis_next_run_at) : undefined">{{ status.analysis_next_run_at ? formatLocalDateTime(status.analysis_next_run_at) : t('workspace.neverRun') }}</strong></div>
      <div><span>{{ t('analysisSettings.latestUrgency') }}</span><strong><span v-if="analyses[0]" class="tm-urgency" :class="`tm-urgency--${analyses[0].urgency.toLowerCase()}`">{{ analyses[0].urgency }}</span><span v-else>—</span></strong></div>
    </div>

    <div class="tm-analysis-layout tm-analysis-layout--results-only">
      <article class="tm-card tm-analysis-history-card">
        <header class="tm-card-header"><div><Sparkles :size="19" /><h2>{{ t('analysisSettings.latestResults') }}</h2></div></header>
        <div v-if="analyses.length" class="tm-analysis-history">
          <button
            v-for="analysis in analyses"
            :key="analysis.id"
            class="tm-analysis-history-row"
            :class="{ 'tm-analysis-history-row--active': selected?.id === analysis.id }"
            @click="selectedId = analysis.id"
          >
            <span class="tm-urgency" :class="`tm-urgency--${analysis.urgency.toLowerCase()}`">{{ analysis.urgency }}</span>
            <strong>{{ t('analysisSettings.messagesAnalyzed', { count: analysis.message_count }) }}</strong>
            <span :title="formatLocalDateTime(analysis.created_at)">{{ formatLocalDateTime(analysis.created_at) }}</span>
          </button>
        </div>
        <div v-else class="tm-empty-state"><Bot :size="30" /><p>{{ t('analysisSettings.noResults') }}</p></div>
      </article>
    </div>

    <IntelligenceGraphPanel :graph="intelligenceGraph" :loading="intelligenceLoading" />

    <AlphaOverviewPanel :overview="alphaOverview" :loading="alphaLoading" />

    <div v-if="selected" class="tm-card tm-analysis-detail tm-analysis-detail--structured tm-analysis-detail--standalone">
          <section class="tm-analysis-summary-card">
            <div class="tm-analysis-detail-heading"><Check :size="18" /><strong>{{ t('analysisSettings.summary') }}</strong></div>
            <p class="tm-analysis-summary-text">{{ selected.summary }}</p>
            <div v-if="selected.urgency_reason" class="tm-analysis-risk-note">
              <AlertTriangle :size="15" /><span>{{ selected.urgency_reason }}</span>
            </div>
          </section>

          <section v-if="selectedTopics.length" class="tm-analysis-block">
            <header><Layers3 :size="16" /><strong>{{ t('analysisSettings.topics') }}</strong></header>
            <div class="tm-analysis-topic-list">
              <article v-for="([topic, summary]) in selectedTopics" :key="topic"><strong>{{ topic }}</strong><p>{{ summary }}</p></article>
            </div>
          </section>

          <section v-if="selectedActors.length" class="tm-analysis-block">
            <header><UsersRound :size="16" /><strong>{{ t('analysisSettings.keyActors') }}</strong></header>
            <div class="tm-analysis-actor-list">
              <article v-for="actor in selectedActors" :key="`${actor.name}-${actor.role}`">
                <div><strong>{{ actor.name }}</strong><span>{{ actor.role }}</span></div>
                <p>{{ actor.context }}</p>
                <small>{{ actor.sentiment }}</small>
              </article>
            </div>
          </section>

          <section v-if="selectedSources.length" class="tm-analysis-block">
            <header><Activity :size="16" /><strong>{{ t('analysisSettings.sources') }}</strong></header>
            <div class="tm-analysis-source-list">
              <div v-for="([source, count]) in selectedSources" :key="source"><span>{{ source }}</span><strong>{{ count }}</strong></div>
            </div>
          </section>

          <section v-if="selectedTimeline.length" class="tm-analysis-block">
            <header><ListTree :size="16" /><strong>{{ t('analysisSettings.timeline') }}</strong></header>
            <div class="tm-analysis-timeline">
              <div v-for="item in selectedTimeline" :key="`${item.time}-${item.event}`"><time>{{ item.time }}</time><span>{{ item.event }}</span></div>
            </div>
          </section>

          <section v-if="selectedContradictions.length" class="tm-analysis-block tm-analysis-block--warning">
            <header><AlertTriangle :size="16" /><strong>{{ t('analysisSettings.contradictions') }}</strong></header>
            <ul><li v-for="item in selectedContradictions" :key="item">{{ item }}</li></ul>
          </section>

          <details class="tm-analysis-raw">
            <summary>{{ t('analysisSettings.rawOutput') }}</summary>
            <pre>{{ selected.raw_text }}</pre>
          </details>
    </div>
  </section>
</template>
